import React, { useMemo, useState } from 'react';
import { formatPhoneNumber, getLocaleDateTimeString, history } from '../../../utils';
import { IHardware } from '../../../ts/interface/hardware.interface';
import { IRental } from '../../../ts/interface/rental.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import ColumnAvatar from '../../../components/Redesing/column-avatar';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  useRentalsRelayQuery,
  mapRelayRentalsToIRentals,
  type RentalQueryFilter,
  type RentalSortInput,
} from '../../../hooks/rentals/query/V2';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import {
  MovaticFieldGroupMultiSelect,
  MovaticFieldGroupDate,
} from '../../../components/Redesing/movatic-field-group';
import { getDateAtLastSecond, getDateWithoutTime } from '../../../helpers/dateHelpers';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';

// Same status options as the Rentals list page.
const filterOptions = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
];

// Table column id -> GraphQL sort field. Only sortable columns are listed;
// location names have no server-side sort field.
const SORT_FIELD_BY_COLUMN: Record<string, RentalSortInput['field']> = {
  start_time: 'START_TIME',
  endTime: 'END_TIME',
};

const columns: IColumn[] = [
  {
    id: 'userName',
    label: 'User',
    notSortable: true,
    format: (row: IRental) => {
      const { user } = row;
      return user ? (
        <ColumnAvatar
          avatar={<PersonIcon />}
          firstRow={
            <Typography color="text.primary" variant="body1">
              {user.full_name}
            </Typography>
          }
          secondRow={
            <Typography color="text.secondary" variant="body2">
              <SeverityPill color={'info'}>
                {formatPhoneNumber(user.phone_number)}
              </SeverityPill>
            </Typography>
          }
        />
      ) : (
        ' '
      );
    },
  },
  {
    id: 'startStationName',
    label: 'Start Location',
    notSortable: true,
    format: (row: IRental) => row.start?.station?.name || ' ',
  },
  {
    id: 'start_time',
    label: 'Start Time',
    format: (row: IRental) =>
      row.start?.time ? getLocaleDateTimeString(row.start.time) : ' ',
  },
  {
    id: 'endStationName',
    label: 'End Location',
    notSortable: true,
    format: (row: IRental) => row.end?.station?.name || ' ',
  },
  {
    id: 'endTime',
    label: 'End Time',
    format: (row: IRental) =>
      row.end?.time ? getLocaleDateTimeString(row.end.time) : ' ',
  },
];

const HardwareRentals = ({ hardware }: { hardware: IHardware }) => {
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortConfig, setSortConfig] = useState<ISortConfig>({
    key: 'start_time',
    direction: 'desc',
  });
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [rentals, setRentals] = useState<IRental[]>([]);
  const [exportData, setExportData] = useState<boolean>(false);

  // Applied filters (drive the query + chips) — same set as the Rentals list
  // page (Status + Start/End Date); the System multi-select is org-only there
  // and irrelevant to a single unit.
  const [status, setStatus] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // Pending selections shown in the Filters modal until "Apply".
  const [preStatus, setPreStatus] = useState<Option[]>([]);
  const [preStartDate, setPreStartDate] = useState<Date | null>(null);
  const [preEndDate, setPreEndDate] = useState<Date | null>(null);

  const debouncedSearch = useSearchDebounce(search);

  // Scoped to this hardware; the search matches the renting user by
  // name/phone, same as the main Rentals page pattern search.
  const filter = useMemo<RentalQueryFilter>(() => {
    const f: RentalQueryFilter = {
      hardwareOldId: hardware?.old_id || undefined,
      pattern: debouncedSearch || undefined,
    };
    // 'active' only -> completed:false; 'completed' only -> completed:true;
    // both/none -> no completed filter. Matches the Rentals list page.
    if (status.includes('active') && !status.includes('completed')) {
      f.completed = false;
    } else if (status.includes('completed') && !status.includes('active')) {
      f.completed = true;
    }
    if (startDate) f.startTimeAfter = getDateWithoutTime(startDate).toISOString();
    if (endDate) f.startTimeBefore = getDateAtLastSecond(endDate).toISOString();
    return f;
  }, [hardware?.old_id, debouncedSearch, status, startDate, endDate]);

  const sort = useMemo<RentalSortInput[]>(() => {
    const field = SORT_FIELD_BY_COLUMN[sortConfig.key] ?? 'START_TIME';
    const order = sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order }];
  }, [sortConfig]);

  const {
    data: rentalsDataGraphQL,
    isLoading,
    isRefetching,
    count,
    hasMore,
    endCursor,
  } = useRentalsRelayQuery(filter, {
    limit: sizePerPage,
    sort,
    enabled: Boolean(hardware?.old_id),
    after: afterCursor || undefined,
  });

  const mappedPage = useMemo<IRental[]>(() => {
    if (!rentalsDataGraphQL) return [];
    return mapRelayRentalsToIRentals(rentalsDataGraphQL) as IRental[];
  }, [rentalsDataGraphQL]);

  // Accumulate cursor pages so the table can slice locally (same pattern as
  // the main Rentals table card).
  useMemo(() => {
    if (!mappedPage.length && !afterCursor) {
      setRentals([]);
      return;
    }
    if (afterCursor) {
      setRentals((prev) => [...prev, ...mappedPage]);
    } else {
      setRentals(mappedPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedPage]);

  const resetPagination = () => {
    setRentals([]);
    setAfterCursor(null);
    setPageNumber(0);
  };

  const items = rentals.length > 0 ? rentals : mappedPage;

  // Chips are derived purely from the applied filters, so they always match the
  // fetched data (and clear when a filter is removed).
  const chips = useMemo<SearchChip[]>(() => {
    const next: SearchChip[] = [];
    status.forEach((value) => {
      const option = filterOptions.find((o) => o.value === value);
      next.push({
        label: 'Status',
        field: 'status',
        value,
        displayValue: option?.label ?? value,
        disabled: false,
      });
    });
    if (startDate) {
      next.push({
        label: 'Start Date',
        field: 'startDate',
        value: startDate,
        displayValue: startDate.toLocaleDateString(),
      });
    }
    if (endDate) {
      next.push({
        label: 'End Date',
        field: 'endDate',
        value: endDate,
        displayValue: endDate.toLocaleDateString(),
      });
    }
    return next;
  }, [status, startDate, endDate]);

  const handleChipDelete = (deletedGroup: SearchChip) => {
    resetPagination();
    if (deletedGroup.label === 'Status') {
      setStatus([]);
      setPreStatus([]);
    }
    if (deletedGroup.label === 'Start Date') {
      setStartDate(null);
      setPreStartDate(null);
    }
    if (deletedGroup.label === 'End Date') {
      setEndDate(null);
      setPreEndDate(null);
    }
  };

  const applyFilters = () => {
    resetPagination();
    setStatus(preStatus.map((o) => o.value.toString()));
    setStartDate(preStartDate);
    setEndDate(preEndDate);
  };

  return (
    <CardView
      isLoading={isLoading && !items.length}
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
        />
      }
      content={
        <PaginationTable
          localSearch={false}
          hasMore={hasMore}
          totalItems={count}
          isLoading={
            isLoading || (pageNumber > 0 && items.length <= pageNumber * sizePerPage)
          }
          isLoadingCache={isRefetching}
          chips={chips}
          handleChipDelete={handleChipDelete}
          onApplyFilter={applyFilters}
          spaceFilters={
            <>
              <MovaticFieldGroupMultiSelect
                label="Status"
                value={preStatus}
                onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                  setPreStatus(newValue);
                }}
                options={filterOptions}
              />
              <MovaticFieldGroupDate
                label="Start Date"
                value={preStartDate}
                onChange={(date: Date | null) => setPreStartDate(date)}
              />
              <MovaticFieldGroupDate
                label="End Date"
                value={preEndDate}
                onChange={(date: Date | null) => setPreEndDate(date)}
              />
            </>
          }
          onRowCLick={(id: string) => history.push(`/rentals/${id}`)}
          exportData={exportData}
          csvFileName={'Hardware_Rentals.csv'}
          onExport={() => setExportData(false)}
          sortDirection={sortConfig.direction}
          sortBy={sortConfig.key}
          onSortChange={(sort) => {
            resetPagination();
            setSortConfig(sort as ISortConfig);
          }}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            resetPagination();
            setSearch(e.target.value);
          }}
          searchText={search}
          columns={columns}
          items={items}
          onPageChange={(page, newPage) => {
            const nextPage = newPage + 1;
            if (items.length && items.length / sizePerPage < nextPage) {
              setAfterCursor(endCursor || null);
            }
            setPageNumber(newPage);
          }}
          onRowsPerPageChange={(row) => {
            const pageSize = Number(row.target.value);
            resetPagination();
            setSizePerPage(pageSize);
          }}
          page={pageNumber}
          rowsPerPage={sizePerPage}
          searchPlaceholder={'Search rentals'}
          noDataText={`No rentals for this ${hardware?.hardware_type_name}`}
        />
      }
      title={'Rentals'}
    />
  );
};

export default HardwareRentals;
